import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAddresses } from '../context/AddressContext';
import api from '../api';
import './ScopeOfWork.css';

export default function ScopeOfWork() {
  const { id: packageId } = useParams();
  const { addresses } = useAddresses();
  const [selectedAddress, setSelectedAddress] = useState('');
  const [totalSpend, setTotalSpend] = useState('');
  const [fileName, setFileName] = useState('');
  const [descriptions, setDescriptions] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingWorksheet, setSavingWorksheet] = useState(false);
  const [error, setError] = useState('');
  const [worksheets, setWorksheets] = useState([]);
  const fileRef = useRef();
  const saveTimer = useRef(null);

  const validAddresses = addresses.filter(a => a.trim());

  // Load saved worksheets for this refi pack
  const loadWorksheets = useCallback(async () => {
    try {
      const res = await api.get('/scope/worksheets', { params: { packageId } });
      setWorksheets(res.data.worksheets || []);
    } catch (err) {
      console.error('Failed to load worksheets:', err);
    }
  }, [packageId]);

  useEffect(() => {
    loadWorksheets();
  }, [loadWorksheets]);

  // Load saved scope when address changes
  useEffect(() => {
    if (!selectedAddress) return;
    api.get('/scope/load', { params: { packageId, address: selectedAddress } })
      .then(res => {
        if (res.data.lineItems?.length > 0) {
          setLineItems(res.data.lineItems);
          setTotalSpend(res.data.totalSpend?.toString() || '');
        } else {
          setLineItems([]);
          setTotalSpend('');
        }
        setFileName('');
        setDescriptions([]);
      })
      .catch(() => {});
  }, [selectedAddress]);

  // Auto-save when line items change
  const saveScope = useCallback(async (items, spend, addr) => {
    if (!addr || items.length === 0) return;
    setSaving(true);
    try {
      await api.post('/scope/save', {
        packageId,
        address: addr,
        totalSpend: parseFloat(spend) || 0,
        lineItems: items,
      });
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  }, []);

  const debouncedSave = useCallback((items, spend, addr) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveScope(items, spend, addr), 1000);
  }, [saveScope]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/scope/parse-xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDescriptions(res.data.descriptions);
    } catch (err) {
      setError('Failed to parse file. Make sure it is a valid .xlsx file.');
    }
  };

  const generateScope = async () => {
    if (!descriptions.length || !totalSpend) {
      setError('Please upload a file and enter the total renovation spend.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/scope/generate', {
        descriptions,
        totalSpend: parseFloat(totalSpend),
      });
      setLineItems(res.data.lineItems);
      // Save immediately after generation
      saveScope(res.data.lineItems, totalSpend, selectedAddress);
    } catch (err) {
      // Surface the server's error message so the user sees overloaded /
      // rate-limit / parse failures clearly instead of a generic retry hint.
      setError(err.response?.data?.error || 'Failed to generate scope. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: field === 'cost' ? Number(value) || 0 : value };
    setLineItems(updated);
    debouncedSave(updated, totalSpend, selectedAddress);
  };

  const addLineItem = () => {
    const updated = [...lineItems, { description: '', cost: 0 }];
    setLineItems(updated);
    debouncedSave(updated, totalSpend, selectedAddress);
  };

  const deleteLineItem = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
    debouncedSave(updated, totalSpend, selectedAddress);
  };

  const currentTotal = lineItems.reduce((sum, item) => sum + (item.cost || 0), 0);
  const targetTotal = parseFloat(totalSpend) || 0;
  const totalMismatch = lineItems.length > 0 && currentTotal !== targetTotal;

  const handleExport = async () => {
    try {
      const res = await api.post('/scope/export', { lineItems, address: selectedAddress, totalSpend: parseFloat(totalSpend) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rehab_Summary_${selectedAddress || 'export'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const text = await err.response?.data?.text?.();
      if (text) {
        try {
          const json = JSON.parse(text);
          setError(json.error);
        } catch {
          setError('Export failed.');
        }
      } else {
        setError('Export failed.');
      }
    }
  };

  const copyToClipboard = () => {
    const text = lineItems.map(item => `${item.description}\t$${item.cost.toLocaleString()}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  const handleSaveToWorksheet = async () => {
    if (!selectedAddress || lineItems.length === 0) return;
    setSavingWorksheet(true);
    setError('');
    try {
      await api.post('/scope/save-worksheet', {
        packageId,
        address: selectedAddress,
        lineItems,
      });
      await loadWorksheets();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save worksheet.';
      setError(msg);
    } finally {
      setSavingWorksheet(false);
    }
  };

  const handleDownloadWorksheet = async (worksheet) => {
    try {
      const res = await api.get(`/scope/worksheets/${worksheet.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', worksheet.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download worksheet.');
    }
  };

  const handleDeleteWorksheet = async (worksheet) => {
    if (!window.confirm(`Delete saved worksheet for ${worksheet.address}?`)) return;
    try {
      await api.delete(`/scope/worksheets/${worksheet.id}`);
      await loadWorksheets();
    } catch {
      setError('Failed to delete worksheet.');
    }
  };

  return (
    <div className="scope-page">
      <h1 className="page-title">Scope of Work</h1>

      {validAddresses.length === 0 ? (
        <div className="warning-box">No addresses saved. Go to the Home tab to add addresses first.</div>
      ) : (
        <div className="scope-layout">
          <div className="scope-main">
            <div className="scope-controls">
              <div className="control-group">
                <label>Property Address</label>
                <select value={selectedAddress} onChange={e => setSelectedAddress(e.target.value)}>
                  <option value="">Select an address...</option>
                  {validAddresses.map((addr, i) => (
                    <option key={i} value={addr}>{addr}</option>
                  ))}
                </select>
              </div>

              <div className="control-group">
                <label>Total Renovation Spend ($)</label>
                <input
                  type="number"
                  placeholder="e.g. 75000"
                  value={totalSpend}
                  onChange={e => setTotalSpend(e.target.value)}
                />
              </div>

              <div className="control-group">
                <label>Upload Rehab Detail File</label>
                <div className="file-upload-row">
                  <button className="btn-upload" onClick={() => fileRef.current.click()}>
                    Upload .xlsx
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  {fileName && <span className="file-name">{fileName}</span>}
                </div>
                {descriptions.length > 0 && (
                  <span className="file-info">{descriptions.length} line items found</span>
                )}
              </div>

              <button
                className="btn-primary btn-generate"
                onClick={generateScope}
                disabled={loading || !descriptions.length || !totalSpend}
              >
                {loading ? 'Generating with AI...' : 'Generate Scope of Work'}
              </button>
            </div>

            {error && <div className="error-box">{error}</div>}

            {lineItems.length > 0 && (
              <div className="scope-results">
                {saving && <div className="save-indicator">Saving...</div>}
                <table className="scope-table">
                  <thead>
                    <tr>
                      <th className="col-num">#</th>
                      <th className="col-desc">Line Item Description</th>
                      <th className="col-cost">Cost</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, i) => (
                      <tr key={i}>
                        <td className="col-num">{i + 1}</td>
                        <td className="col-desc">
                          <input
                            type="text"
                            value={item.description}
                            onChange={e => updateItem(i, 'description', e.target.value)}
                          />
                        </td>
                        <td className="col-cost">
                          <input
                            type="number"
                            value={item.cost}
                            onChange={e => updateItem(i, 'cost', e.target.value)}
                          />
                        </td>
                        <td className="col-actions">
                          <button
                            className="btn-row-delete"
                            onClick={() => deleteLineItem(i)}
                            title="Delete line item"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td></td>
                      <td className="total-label">Total</td>
                      <td className={`total-value ${totalMismatch ? 'mismatch' : ''}`}>
                        ${currentTotal.toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                    {totalMismatch && (
                      <tr>
                        <td></td>
                        <td colSpan="3" className="mismatch-warning">
                          Total does not match target of ${targetTotal.toLocaleString()} (off by ${Math.abs(currentTotal - targetTotal).toLocaleString()})
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>

                <button className="btn-add-row" onClick={addLineItem}>+ Add line item</button>

                <div className="scope-actions">
                  <button
                    className="btn-primary"
                    onClick={handleSaveToWorksheet}
                    disabled={savingWorksheet || !selectedAddress}
                  >
                    {savingWorksheet ? 'Saving...' : 'Save to Worksheet'}
                  </button>
                  <button className="btn-secondary" onClick={handleExport}>Export to .xlsx</button>
                  <button className="btn-secondary" onClick={copyToClipboard}>Copy to Clipboard</button>
                </div>
              </div>
            )}
          </div>

          <aside className="scope-sidebar">
            <h2 className="sidebar-title">Scope of Work for this Refi Pack</h2>
            {worksheets.length === 0 ? (
              <p className="sidebar-empty">No saved worksheets yet. Click "Save to Worksheet" after generating a scope.</p>
            ) : (
              <ul className="worksheet-list">
                {worksheets.map(ws => (
                  <li key={ws.id} className="worksheet-item">
                    <div className="worksheet-info">
                      <div className="worksheet-address">{ws.address}</div>
                      <div className="worksheet-meta">{ws.file_name}</div>
                    </div>
                    <div className="worksheet-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleDownloadWorksheet(ws)}
                        title="Download"
                      >
                        ↓
                      </button>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDeleteWorksheet(ws)}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
